import React, { useState, useEffect } from 'react';
import { X, ShoppingCart, Heart, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ProductImageGallery, ProductGalleryThumbs } from './ProductImageGallery';
import { WhatsAppButton } from './WhatsAppButton';
import { trackProductView } from '../lib/analytics';
import { toast } from 'sonner';

export const ProductDetailModal = ({ product, isOpen, onClose, onAddToCart, onAddToWishlist }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (isOpen && product) {
      setActiveIndex(0);
      trackProductView(product);
    }
  }, [isOpen, product]);

  // Keyboard arrow keys navigate the gallery while the modal is open.
  useEffect(() => {
    if (!isOpen || !product) return undefined;
    const total = (product.images && product.images.length) || (product.image ? 1 : 0);
    if (total < 2) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowRight') setActiveIndex(i => (i + 1) % total);
      else if (e.key === 'ArrowLeft') setActiveIndex(i => (i - 1 + total) % total);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, product]);

  if (!product) return null;

  const handleAddToCart = () => {
    onAddToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  const handleAddToWishlist = () => {
    onAddToWishlist(product);
    toast.success(`${product.name} added to wishlist!`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl bg-[#FAF9F7] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">{product.name}</DialogTitle>
          <DialogDescription className="sr-only">
            {product.category} — ₹{product.price}
          </DialogDescription>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 text-[#2C2C2C] hover:text-[#C4B5A0]"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-8 mt-4">
          {/* Left: Product Image Gallery */}
          <div className="space-y-3">
            <div className="relative aspect-[3/4] bg-[#F5F3F0] rounded-lg overflow-hidden">
              <ProductImageGallery
                images={product.images}
                fallback={product.image}
                alt={product.name}
                activeIndex={activeIndex}
                onIndexChange={setActiveIndex}
                className="w-full h-full"
              />
              {product.discount && (
                <Badge className="absolute top-4 left-4 z-20 bg-[#6B4E37] text-white font-montserrat text-xs px-3 py-1">
                  {product.discount}
                </Badge>
              )}
            </div>
            <ProductGalleryThumbs
              images={product.images}
              fallback={product.image}
              activeIndex={activeIndex}
              onSelect={setActiveIndex}
            />
          </div>

          {/* Right: Product Details */}
          <div className="space-y-6">
            {/* Category */}
            <p className="text-[#8B7D6B] font-montserrat text-sm tracking-wider uppercase">
              {product.category}
            </p>

            {/* Product Name */}
            <h2 className="text-3xl font-seasons font-light text-[#2C2C2C] leading-tight">
              {product.name}
            </h2>

            {/* Rating */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.floor(product.rating)
                        ? 'fill-[#C4B5A0] text-[#C4B5A0]'
                        : 'text-[#D4C4B0]'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-[#8B7D6B] font-montserrat">
                {product.rating} ({product.reviews} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="flex items-center gap-4">
              <span className="text-3xl font-seasons text-[#2C2C2C]">₹{product.price}</span>
              {product.originalPrice && (
                <span className="text-lg font-montserrat text-[#8B7D6B] line-through">
                  ₹{product.originalPrice}
                </span>
              )}
            </div>

            <Separator className="bg-[#D4C4B0]" />

            {/* Product Description */}
            <div className="space-y-4">
              <h3 className="font-montserrat text-sm tracking-wider uppercase text-[#6B4E37] font-medium">
                Product Details
              </h3>
              
              <div className="space-y-3 text-[#2C2C2C] font-montserrat">
                <div>
                  <span className="font-medium text-[#6B4E37]">Fabric: </span>
                  <span>{product.fabric}</span>
                </div>
                <div>
                  <span className="font-medium text-[#6B4E37]">Style: </span>
                  <span>{product.style}</span>
                </div>
                <div>
                  <span className="font-medium text-[#6B4E37]">Color: </span>
                  <span>{product.color}</span>
                </div>
                <div>
                  <span className="font-medium text-[#6B4E37]">Work: </span>
                  <span>{product.work}</span>
                </div>
              </div>

              <p className="text-[#2C2C2C] font-montserrat leading-relaxed">
                {product.description}
              </p>
            </div>

            <Separator className="bg-[#D4C4B0]" />

            {/* Features */}
            <div className="space-y-2">
              <h3 className="font-montserrat text-sm tracking-wider uppercase text-[#6B4E37] font-medium">
                Features
              </h3>
              <ul className="space-y-2 text-sm text-[#2C2C2C] font-montserrat">
                <li className="flex items-start gap-2">
                  <span className="text-[#C4B5A0] mt-1">•</span>
                  <span>Premium quality fabric with rich texture</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C4B5A0] mt-1">•</span>
                  <span>Intricate handcrafted embroidery work</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C4B5A0] mt-1">•</span>
                  <span>Comes with matching dupatta</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C4B5A0] mt-1">•</span>
                  <span>Perfect for festive and special occasions</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                data-testid="modal-add-to-cart"
                disabled={product.stock <= 0}
                className={`flex-1 font-montserrat tracking-wider uppercase py-6 transition-all duration-300 ${
                  product.stock <= 0
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-[#C4B5A0] hover:bg-[#B0A090] text-white hover:shadow-lg'
                }`}
                onClick={() => { if (product.stock <= 0) return; handleAddToCart(); }}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                {product.stock <= 0 ? 'Out of Stock' : 'Add to Cart'}
              </Button>
              <Button
                variant="outline"
                className="border-2 border-[#C4B5A0] text-[#C4B5A0] hover:bg-[#C4B5A0] hover:text-white font-montserrat tracking-wider uppercase px-6 py-6 transition-all duration-300"
                onClick={handleAddToWishlist}
              >
                <Heart className="h-4 w-4" />
              </Button>
            </div>
            
            {/* WhatsApp Buttons */}
            <div className="pt-2 flex gap-2">
              <WhatsAppButton variant="product" productName={product.name} className="flex-1 py-4" />
              <WhatsAppButton 
                variant="share" 
                productName={product.name} 
                productPrice={product.price}
                className="border border-[#25D366] px-4"
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
